// Product verticals — frontend mirror of `backend/constants/productVertical.js`.
//
// CRA doesn't share modules with the backend, so this file is the source of
// truth on the frontend side. The two must stay in lockstep — if you add a
// vertical here, add it there too. Phase 1 of the marketplace-V1 rework
// (see docs/MARKETPLACE_V1.md §16).
//
// Verticals are a MORE GRANULAR discriminator than kind. `kind` describes
// the fulfillment shape (physical/event); `vertical` describes the
// storefront + admin domain (groceries/fashion/electronics/events).

export const PRODUCT_VERTICAL = Object.freeze({
    GROCERIES:   'groceries',
    FASHION:     'fashion',
    ELECTRONICS: 'electronics',
    EVENTS:      'events',
})

export const PRODUCT_VERTICALS = Object.freeze(Object.values(PRODUCT_VERTICAL))

// Display metadata — kept here so nav bars, storefront cards, and admin
// menus don't invent conflicting labels.
export const VERTICAL_META = Object.freeze({
    [PRODUCT_VERTICAL.GROCERIES]:   { emoji: '🛒', label: 'Groceries',   route: '/groceries',   plural: 'Groceries' },
    [PRODUCT_VERTICAL.FASHION]:     { emoji: '👕', label: 'Fashion',     route: '/fashion',     plural: 'Fashion' },
    [PRODUCT_VERTICAL.ELECTRONICS]: { emoji: '📱', label: 'Electronics', route: '/electronics', plural: 'Electronics' },
    [PRODUCT_VERTICAL.EVENTS]:      { emoji: '🎟️', label: 'Events',      route: '/events',      plural: 'Events' },
})

// Resolve a product's vertical. Explicit `vertical` wins. Otherwise infer
// from `kind`:
//   - kind === 'event' → EVENTS
//   - anything else    → ELECTRONICS (default catch-all for legacy physical)
//
// The ELECTRONICS default matches the backend's `resolveVertical()` behaviour.
export function resolveVertical(product) {
    if (!product) return PRODUCT_VERTICAL.ELECTRONICS
    if (product.vertical && PRODUCT_VERTICALS.includes(product.vertical)) return product.vertical
    if (product.kind === 'event') return PRODUCT_VERTICAL.EVENTS
    return PRODUCT_VERTICAL.ELECTRONICS
}

// Predicates. Use these in JSX/handlers.
export const isGroceriesProduct   = (product) => resolveVertical(product) === PRODUCT_VERTICAL.GROCERIES
export const isFashionProduct     = (product) => resolveVertical(product) === PRODUCT_VERTICAL.FASHION
export const isElectronicsProduct = (product) => resolveVertical(product) === PRODUCT_VERTICAL.ELECTRONICS
export const isEventsProduct      = (product) => resolveVertical(product) === PRODUCT_VERTICAL.EVENTS

// Physical vertical = anything that ships and has inventory. Everything
// except events.
export const isPhysicalVertical = (product) => resolveVertical(product) !== PRODUCT_VERTICAL.EVENTS

// Feature flag mirror — build-time env var, embedded by CRA. When true the
// storefront + admin nav render the per-vertical structure. Off by default
// until the frontend surfaces catch up (Phase 5+).
export const isVerticalRoutingEnabled = () => {
    return String(process.env.REACT_APP_VERTICAL_ROUTING_ENABLED || '').toLowerCase() === 'true'
}
